import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { useCurrentOrganization } from '@/components/features/membership/membership-provider';
import { can } from '@/components/features/permission/can';
import {
  ScheduleForm,
  SchedulingMode,
} from '@/components/features/schedule/forms/schedule-form';
import { Card, CardContent } from '@/components/ui/card';
import {
  Page,
  PageContent,
  PageHeader,
  PageHeaderDescription,
  PageHeaderInfo,
  PageHeaderTitle,
} from '@/components/ui/layout/page';
import { createScheduleMutation } from '@/mutations/createScheduleMutation';
import { useMutation } from '@tanstack/react-query';

const defaultValuesSchema = z.object({
  name: z.string().optional(),
  description: z.string().optional(),
  mode: z.nativeEnum(SchedulingMode).optional(),
});

export const Route = createFileRoute('/_authenticated/schedule/create')({
  component: RouteComponent,
  validateSearch: defaultValuesSchema,
  beforeLoad: () => {
    if (!can(['schedule:create'])) {
      return redirect({ to: '/schedule' });
    }
  },
});

function RouteComponent() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { organizationId } = useCurrentOrganization();
  const search = Route.useSearch();

  const { mutate: createSchedule, isPending: isCreatingSchedule } = useMutation(
    {
      ...createScheduleMutation(organizationId),
      onSuccess: (response) => {
        if (response.isOk()) {
          toast.success(t('authenticated.schedule.create.toast.success'));
          navigate({
            to: '/schedule/$scheduleId',
            params: {
              scheduleId: response.value.schedule.scheduleId,
            },
          });
        } else {
          toast.error(t('authenticated.schedule.create.toast.genericError'));
        }
      },
    }
  );

  return (
    <Page>
      <PageHeader>
        <PageHeaderInfo>
          <PageHeaderTitle>
            {t('authenticated.schedule.create.title')}
          </PageHeaderTitle>
          <PageHeaderDescription>
            {t('authenticated.schedule.create.description')}
          </PageHeaderDescription>
        </PageHeaderInfo>
      </PageHeader>

      <PageContent>
        <Card>
          <CardContent className="pt-6">
            <ScheduleForm
              onSubmit={createSchedule}
              submitLabel={t('authenticated.schedule.create.submit')}
              isSubmitting={isCreatingSchedule}
              defaultValues={{
                name: search.name,
                description: search.description,
                mode: search.mode,
              }}
            />
          </CardContent>
        </Card>
      </PageContent>
    </Page>
  );
}
